<script setup>
const props = defineProps({
  modelValue: {
    type: Array,
    default: () => []
  }
});

const emit = defineEmits(['update:modelValue', 'error']);

const selectedFiles = ref([]);
const filePreviewUrls = ref([]);
const invalidFileError = ref('');
const fileInput = ref(null);

function handleFileSelect(event) {
  invalidFileError.value = "";

  if (event.target.files && event.target.files.length > 0) {
    const files = Array.from(event.target.files);

    // Filter for only image files
    const validFiles = files.filter(file => file.type.startsWith('image/'));

    if (validFiles.length !== files.length) {
      invalidFileError.value = "Only image files are allowed. Some files were removed.";
    }

    addFiles(validFiles);
  }
}

// Handle paste event
function handlePaste(event) {
  invalidFileError.value = "";

  // Get items from clipboard
  const items = (event.clipboardData || event.originalEvent.clipboardData).items;

  if (!items) return;

  const imageFiles = [];

  // Process each item
  for (const item of items) {
    if (item.type.indexOf('image') === 0) {
      const blob = item.getAsFile();
      if (blob) {
        // Create a file with a unique name
        const file = new File([blob], `pasted-image-${Date.now()}.png`, { type: blob.type });
        imageFiles.push(file);
      }
    }
  }

  addFiles(imageFiles);
}

// Add files to selectedFiles array and generate previews
function addFiles(files) {
  // Add files to selectedFiles array
  selectedFiles.value = [...selectedFiles.value, ...files];

  // Generate previews for the new files
  files.forEach(file => {
    const reader = new FileReader();
    reader.onload = (e) => {
      filePreviewUrls.value.push({
        name: file.name,
        url: e.target.result
      });
    };
    reader.readAsDataURL(file);
  });

  emit('update:modelValue', selectedFiles.value);
}

// Remove a file
function removeFile(index) {
  selectedFiles.value.splice(index, 1);
  filePreviewUrls.value.splice(index, 1);
  emit('update:modelValue', selectedFiles.value);
}

// Trigger paste from button
function triggerPaste() {
  navigator.clipboard.read().then(clipboardItems => {
    for (const clipboardItem of clipboardItems) {
      for (const type of clipboardItem.types) {
        if (type.startsWith('image/')) {
          clipboardItem.getType(type).then(blob => {
            const file = new File([blob], `pasted-image-${Date.now()}.png`, { type });
            addFiles([file]);
          });
        }
      }
    }
  }).catch(err => {
    console.error('Failed to read clipboard contents: ', err);
    invalidFileError.value = "Failed to access clipboard. Please use Ctrl+V in the paste area or try another method.";
    emit('error', invalidFileError.value);
  });
}

// Watch for changes in modelValue
watch(() => props.modelValue, (newValue) => {
  if (newValue !== selectedFiles.value) {
    selectedFiles.value = newValue;
  }
}, { deep: true });

onMounted(() => {
  // Global paste event listener for img upload
  window.addEventListener('paste', handlePaste);
});

// Clean up event listener on unmount
onUnmounted(() => {
  window.removeEventListener('paste', handlePaste);
});
</script>

<template>
  <div class="space-y-1">
    <!-- Paste Area -->
    <div ref="pasteArea" @click="triggerPaste"
      class="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors mb-3 border-gray-300 hover:border-gray-400">
      <div class="flex flex-col items-center justify-center py-2">
        <UIcon name="i-heroicons-clipboard-document" class="h-8 w-8 text-gray-400 mb-2" />
        <p class="text-sm font-medium">Paste image here (Ctrl+V)</p>
      </div>
    </div>

    <!-- Upload -->
    <UButton color="neutral" variant="outline" icon="i-heroicons-photo" class="grow" size="sm"
      @click="$refs.fileInput.click()">
      Or, Upload Image
    </UButton>
    <input ref="fileInput" type="file" multiple accept="image/*" class="hidden" @change="handleFileSelect" />

    <p v-if="invalidFileError" class="text-red-500 text-sm">{{ invalidFileError }}</p>

    <!-- File Previews -->
    <div v-if="filePreviewUrls.length > 0" class="mt-2 mb-4">
      <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        <div v-for="(preview, index) in filePreviewUrls" :key="index" class="relative group">
          <div class="aspect-square rounded-lg overflow-hidden border bg-gray-100">
            <img :src="preview.url" :alt="preview.name" class="w-full h-full object-cover" />
          </div>
          <UButton color="error" variant="solid" icon="i-heroicons-trash" size="xs" class="absolute top-1 right-1"
            @click="removeFile(index)" />
          <div class="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs p-1 truncate">
            {{ preview.name }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>